import Button from "@material-ui/core/Button";
import { categoryUrl } from "@saleor/categories/urls";
import { collectionUrl } from "@saleor/collections/urls";
import AssignCategoriesDialog from "@saleor/components/AssignCategoryDialog";
import AssignCollectionDialog from "@saleor/components/AssignCollectionDialog";
import AssignProductDialog from "@saleor/components/AssignProductDialog";
import AssignVariantDialog from "@saleor/components/AssignVariantDialog";
import { DEFAULT_INITIAL_SEARCH_DATA, PAGINATE_BY } from "@saleor/config";
import CornermarkDeleteDialg from "@saleor/cornermarks/components/CornermarkDeleteDialog";
import CornermarkPage, {
  CornerMarkDetailsPageTab,
  CornermarkPageFormData
} from "@saleor/cornermarks/components/CornermarkPage";
import {
  useCornerMarkBatchSetMutation,
  useCornerMarkCancelSetMutation,
  useCornermarkDeleteMutation,
  useCornermarkUpdateMutation
} from "@saleor/cornermarks/mutations";
import { useCornermarkDetailsQuery } from "@saleor/cornermarks/queries";
import {
  cornermarkListUrl,
  cornermarkUrl,
  CornermarkUrlDialog,
  CornermarkUrlQueryParams
} from "@saleor/cornermarks/urls";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import { commonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { productUrl, productVariantEditUrl } from "@saleor/products/urls";
import useCategorySearch from "@saleor/searches/useCategorySearch";
import useCollectionSearch from "@saleor/searches/useCollectionSearch";
import useProductSearch from "@saleor/searches/useProductSearch";
import useProductVariantSearch from "@saleor/searches/useProductVariantSearch";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import { mapEdgesToItems } from "@saleor/utils/maps";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

interface CornermarkDetailsProps {
  id: string;
  params: CornermarkUrlQueryParams;
}

const CornermarkDetails: React.FC<CornermarkDetailsProps> = ({
  id,
  params
}) => {
  const navigate = useNavigator();
  const {
    loadMore: loadMoreCategories,
    search: searchCategories,
    result: searchCategoriesOpts
  } = useCategorySearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });
  const {
    loadMore: loadMoreCollections,
    search: searchCollections,
    result: searchCollectionsOpts
  } = useCollectionSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });
  const {
    loadMore: loadMoreProducts,
    search: searchProducts,
    result: searchProductsOpts
  } = useProductSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });
  const {
    loadMore: loadMoreProductVariants,
    search: searchProductVariants,
    result: searchProductVariantsOpts
  } = useProductVariantSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });
  const paginationState = createPaginationState(PAGINATE_BY, params);
  const paginate = usePaginator();

  const notify = useNotifier();
  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );
  const intl = useIntl();
  const [openModal, closeModal] = createDialogActionHandlers<
    CornermarkUrlDialog,
    CornermarkUrlQueryParams
  >(navigate, params => cornermarkUrl(id, params), params);

  const { data, loading, refetch } = useCornermarkDetailsQuery({
    variables: {
      id,
      ...paginationState
    }
  });

  const [cornermarkDelete, cornermarkDeleteOpts] = useCornermarkDeleteMutation({
    onCompleted: data => {
      if (data.cornerMarkDelete.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "cornermark delete"
          })
        });
        navigate(cornermarkListUrl());
      }
    }
  });

  const [cornermarkUpdate, cornermarkUpdateOpts] = useCornermarkUpdateMutation({
    onCompleted: data => {
      if (data.cornerMarkUpdate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
      }
    }
  });

  const handleUpdate = async (data: CornermarkPageFormData) => {
    const input = {
      ...data
    };

    const result = await cornermarkUpdate({
      variables: {
        id,
        input
      }
    });

    return result.data.cornerMarkUpdate.errors;
  };
  const [
    cornerMarkCancelSet,
    cornerMarkCancelSetOpts
  ] = useCornerMarkCancelSetMutation({
    onCompleted: data => {
      if (data.cornerMarkCancelSet.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Unassociation Success"
          })
        });
        refetch();
      }
    }
  });
  const [
    cornerMarkBatchSet,
    cornerMarkBatchSetOpts
  ] = useCornerMarkBatchSetMutation({
    onCompleted: data => {
      if (data.cornerMarkBatchSet.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Association Success"
          })
        });
        closeModal();
        refetch();
      }
    }
  });
  const changeTab = (tab: CornerMarkDetailsPageTab) => {
    reset();
    navigate(
      cornermarkUrl(id, {
        activeTab: tab
      })
    );
  };

  const tabPageInfo =
    params.activeTab === CornerMarkDetailsPageTab.categories
      ? maybe(() => data.cornerMark.categories.pageInfo)
      : params.activeTab === CornerMarkDetailsPageTab.collections
      ? maybe(() => data.cornerMark.collections.pageInfo)
      : params.activeTab === CornerMarkDetailsPageTab.products
      ? maybe(() => data.cornerMark.products.pageInfo)
      : maybe(() => data.cornerMark.variants.pageInfo);

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    tabPageInfo,
    paginationState,
    params
  );

  return (
    <>
      <CornermarkPage
        errors={cornermarkUpdateOpts.data?.cornerMarkUpdate.errors || []}
        cornermark={maybe(() => data.cornerMark)}
        disabled={loading || cornerMarkCancelSetOpts.loading}
        onBack={() => navigate(cornermarkListUrl())}
        saveButtonBarState={cornermarkUpdateOpts.status}
        onDelete={() => openModal("remove")}
        onSubmit={handleUpdate}
        title={"details"}
        activeTab={params.activeTab}
        onTabClick={changeTab}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        toggle={toggle}
        toggleAll={toggleAll}
        isChecked={isSelected}
        selected={listElements.length}
        pageInfo={pageInfo}
        onVariantAssign={() => openModal("assign-variant")}
        onCategoryAssign={() => openModal("assign-category")}
        onCollectionAssign={() => openModal("assign-collection")}
        onProductAssign={() => openModal("assign-product")}
        onCollectionClick={id => () => navigate(collectionUrl(id))}
        onCategoryClick={id => () => navigate(categoryUrl(id))}
        onProductClick={id => () => navigate(productUrl(id))}
        onVariantClick={(productId, variantId) => () =>
          navigate(productVariantEditUrl(productId, variantId))}
        onProductUnassign={productId =>
          cornerMarkCancelSet({
            variables: {
              productId
            }
          })
        }
        onVariantUnassign={variantId =>
          cornerMarkCancelSet({
            variables: {
              variantId
            }
          })
        }
        onCollectionUnassign={collectionId =>
          cornerMarkCancelSet({
            variables: {
              collectionId
            }
          })
        }
        onCategoryUnassign={categoryId =>
          cornerMarkCancelSet({
            variables: {
              categoryId
            }
          })
        }
        categoryListToolbar={
          <Button
            color="primary"
            onClick={() =>
              openModal("unassign-category", {
                ids: listElements
              })
            }
          >
            <FormattedMessage
              defaultMessage="Unassign"
              description="unassign category from voucher, button"
              id="voucherDetailsUnassignCategory"
            />
          </Button>
        }
        collectionListToolbar={
          <Button
            color="primary"
            onClick={() =>
              openModal("unassign-collection", {
                ids: listElements
              })
            }
          >
            <FormattedMessage
              defaultMessage="Unassign"
              description="unassign collection from voucher, button"
              id="voucherDetailsUnassignCollection"
            />
          </Button>
        }
        productListToolbar={
          <Button
            color="primary"
            onClick={() =>
              openModal("unassign-product", {
                ids: listElements
              })
            }
          >
            <FormattedMessage
              defaultMessage="Unassign"
              description="unassign product from voucher, button"
              id="voucherDetailsUnassignProduct"
            />
          </Button>
        }
        variantListToolbar={
          <Button
            color="primary"
            onClick={() =>
              openModal("unassign-variant", {
                ids: listElements
              })
            }
          >
            <FormattedMessage
              defaultMessage="Unassign"
              description="unassign product sku from sale, button"
              id="voucherDetailsUnassignVariant"
            />
          </Button>
        }
      />
      <CornermarkDeleteDialg
        open={params.action === "remove"}
        title={maybe(() => data.cornerMark.titleText, "……")}
        confirmButtonState={cornermarkDeleteOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          cornermarkDelete({
            variables: {
              id
            }
          })
        }
      />
      <AssignCategoriesDialog
        categories={maybe(() =>
          searchCategoriesOpts.data.search.edges
            ?.map(edge => edge.node)
            .filter(suggestedCategory => suggestedCategory.id)
        )}
        confirmButtonState={cornerMarkBatchSetOpts.status}
        hasMore={searchCategoriesOpts.data?.search.pageInfo.hasNextPage}
        open={params.action === "assign-category"}
        onFetch={searchCategories}
        onFetchMore={loadMoreCategories}
        loading={searchCategoriesOpts.loading}
        onClose={closeModal}
        onSubmit={categories =>
          cornerMarkBatchSet({
            variables: {
              id,
              categoryIds: categories
            }
          })
        }
      />
      <AssignCollectionDialog
        collections={maybe(() =>
          searchCollectionsOpts.data.search.edges
            ?.map(edge => edge.node)
            .filter(suggestedCategory => suggestedCategory.id)
        )}
        confirmButtonState={cornerMarkBatchSetOpts.status}
        hasMore={searchCollectionsOpts.data?.search.pageInfo.hasNextPage}
        open={params.action === "assign-collection"}
        onFetch={searchCollections}
        onFetchMore={loadMoreCollections}
        loading={searchCollectionsOpts.loading}
        onClose={closeModal}
        onSubmit={collections =>
          cornerMarkBatchSet({
            variables: {
              id,
              collectionIds: collections
            }
          })
        }
      />
      <AssignVariantDialog
        variants={mapEdgesToItems(
          searchProductVariantsOpts?.data?.search
        )?.filter(suggestedVariant => suggestedVariant.id)}
        confirmButtonState={cornerMarkBatchSetOpts.status}
        hasMore={searchProductVariantsOpts.data?.search?.pageInfo.hasNextPage}
        open={params.action === "assign-variant"}
        onFetch={value => {
          // 只判断数字 还有为空的
          const r = /^\d+$/.test(value);
          if (r || value === "") {
            searchProductVariants(value);
          }
        }}
        onFetchMore={loadMoreProductVariants}
        loading={searchProductVariantsOpts.loading}
        onClose={closeModal}
        onSubmit={variants =>
          cornerMarkBatchSet({
            variables: {
              id,
              variantIds: variants?.map(variant => variant.id)
            }
          })
        }
      />
      <AssignProductDialog
        products={mapEdgesToItems(searchProductsOpts?.data?.search)?.filter(
          suggestedProduct => suggestedProduct.id
        )}
        confirmButtonState={cornerMarkBatchSetOpts.status}
        hasMore={searchProductsOpts.data?.search?.pageInfo.hasNextPage}
        open={params.action === "assign-product"}
        onFetch={searchProducts}
        onFetchMore={loadMoreProducts}
        loading={searchProductsOpts.loading}
        onClose={closeModal}
        onSubmit={products =>
          cornerMarkBatchSet({
            variables: {
              ...paginationState,
              id,
              productIds: products?.map(product => product.id)
            }
          })
        }
      />
    </>
  );
};

export default CornermarkDetails;
